import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, Home, Heart, Package, Truck, Plus, X, Check, 
  GraduationCap, PlayCircle, FileText, ClipboardCheck,
  Edit, Trash2, Users, ChevronRight, AlertCircle, Star
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../services/api';

// Badge definitions with icons
const BADGE_CONFIG = {
  cleaner: { icon: Home, color: 'bg-[#D4AF37]/10 text-[#D4AF37] border-blue-300', label: 'Cleaner' },
  private_concierge: { icon: Heart, color: 'bg-[#F4C2C2]/15 text-purple-700 border-purple-300', label: 'Concierge' },
  social_engagement: { icon: Heart, color: 'bg-pink-100 text-pink-700 border-pink-300', label: 'Social' },
  errand_runner: { icon: Package, color: 'bg-amber-100 text-amber-700 border-amber-300', label: 'Errand Runner' },
  courier: { icon: Truck, color: 'bg-[var(--success)]/15 text-green-700 border-green-300', label: 'Courier' }
};

const CertificationCoursesTab = ({ workers }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseProgress, setCourseProgress] = useState([]);
  
  // Custom badges state
  const [customBadges, setCustomBadges] = useState([]);
  const [showBadgeCreateModal, setShowBadgeCreateModal] = useState(false);
  const [newBadgeForm, setNewBadgeForm] = useState({
    name: '',
    description: '',
    icon: 'award',
    color: 'gold'
  });
  const [activeTab, setActiveTab] = useState('courses'); // 'courses' or 'badges'
  
  // Course form state
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    badge_id: 'cleaner',
    passing_score: 80,
    is_required: false,
    videos: [],
    policies: [],
    test_questions: []
  });

  // Badge assignment modal
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerBadges, setWorkerBadges] = useState([]);

  useEffect(() => {
    fetchCourses();
    fetchCustomBadges();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/badges/courses');
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomBadges = async () => {
    try {
      const response = await api.get('/badges/definitions');
      setCustomBadges(response.data.badges || []);
    } catch (error) {
      console.error('Failed to fetch badges:', error);
    }
  };

  const handleCreateBadge = async () => {
    if (!newBadgeForm.name.trim()) {
      toast.error('Please enter a badge name');
      return;
    }
    try {
      await api.post('/badges/definitions', newBadgeForm);
      toast.success('Badge created!');
      setShowBadgeCreateModal(false);
      setNewBadgeForm({ name: '', description: '', icon: 'award', color: 'gold' });
      fetchCustomBadges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create badge');
    }
  };

  const handleDeleteBadge = async (badgeId) => {
    if (!window.confirm('Delete this badge? Workers who have it will lose it.')) return;
    try {
      await api.delete(`/badges/definitions/${badgeId}`);
      toast.success('Badge deleted');
      fetchCustomBadges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete badge');
    }
  };

  const handleSaveCourse = async () => {
    if (!courseForm.title.trim()) {
      toast.error('Please enter a course title');
      return;
    }

    try {
      if (editingCourse) {
        await api.put(`/badges/courses/${editingCourse.id}`, courseForm);
        toast.success('Course updated');
      } else {
        await api.post('/badges/courses', courseForm);
        toast.success('Course created');
      }
      setShowCourseModal(false);
      setEditingCourse(null);
      resetCourseForm();
      fetchCourses();
    } catch (error) {
      toast.error('Failed to save course');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Delete this certification course? This cannot be undone.')) return;
    try {
      await api.delete(`/badges/courses/${courseId}`);
      toast.success('Course deleted');
      fetchCourses();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const resetCourseForm = () => {
    setCourseForm({
      title: '',
      description: '',
      badge_id: 'cleaner',
      passing_score: 80,
      is_required: false,
      videos: [],
      policies: [],
      test_questions: []
    });
  };

  const openEditCourse = (course) => {
    setEditingCourse(course);
    setCourseForm({
      title: course.title,
      description: course.description || '',
      badge_id: course.badge_id,
      passing_score: course.passing_score || 80,
      is_required: course.is_required || false,
      videos: course.videos || [],
      policies: course.policies || [],
      test_questions: course.test_questions || []
    });
    setShowCourseModal(true);
  };

  const viewCourseProgress = async (course) => {
    setSelectedCourse(course);
    try {
      const response = await api.get(`/badges/courses/${course.id}/progress`);
      setCourseProgress(response.data.progress || []);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
      setCourseProgress([]);
    }
  };

  // Badge management functions
  const openBadgeModal = async (worker) => {
    setSelectedWorker(worker);
    try {
      const response = await api.get(`/badges/worker/${worker.id}`);
      setWorkerBadges(response.data.badges || []);
    } catch (error) {
      setWorkerBadges([]);
    }
    setShowBadgeModal(true);
  };

  const assignBadge = async (badgeId) => {
    try {
      await api.post(`/badges/worker/${selectedWorker.id}/assign?badge_id=${badgeId}`);
      toast.success('Badge assigned!');
      // Refresh badges
      const response = await api.get(`/badges/worker/${selectedWorker.id}`);
      setWorkerBadges(response.data.badges || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign badge');
    }
  };

  const revokeBadge = async (badgeId) => {
    if (!window.confirm('Revoke this badge from the worker?')) return;
    try {
      await api.delete(`/badges/worker/${selectedWorker.id}/revoke/${badgeId}`);
      toast.success('Badge revoked');
      const response = await api.get(`/badges/worker/${selectedWorker.id}`);
      setWorkerBadges(response.data.badges || []);
    } catch (error) {
      toast.error('Failed to revoke badge');
    }
  };

  // Add video to course
  const addVideo = () => {
    setCourseForm({
      ...courseForm,
      videos: [...courseForm.videos, { id: Date.now().toString(), title: '', url: '', duration_minutes: 10 }]
    });
  };

  const updateVideo = (index, field, value) => {
    const updated = [...courseForm.videos];
    updated[index][field] = value;
    setCourseForm({ ...courseForm, videos: updated });
  };

  const removeVideo = (index) => {
    setCourseForm({ ...courseForm, videos: courseForm.videos.filter((_, i) => i !== index) });
  };

  // Add policy to course
  const addPolicy = () => {
    setCourseForm({
      ...courseForm,
      policies: [...courseForm.policies, { id: Date.now().toString(), title: '', content: '', requires_signature: true }]
    });
  };

  const updatePolicy = (index, field, value) => {
    const updated = [...courseForm.policies];
    updated[index][field] = value;
    setCourseForm({ ...courseForm, policies: updated });
  };

  const removePolicy = (index) => {
    setCourseForm({ ...courseForm, policies: courseForm.policies.filter((_, i) => i !== index) });
  };

  // Add test question
  const addQuestion = () => {
    setCourseForm({
      ...courseForm,
      test_questions: [...courseForm.test_questions, { 
        id: Date.now().toString(), 
        question: '', 
        options: ['', '', '', ''], 
        correct_answer: 0 
      }]
    });
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...courseForm.test_questions];
    updated[index][field] = value;
    setCourseForm({ ...courseForm, test_questions: updated });
  };

  const updateQuestionOption = (qIndex, oIndex, value) => {
    const updated = [...courseForm.test_questions];
    updated[qIndex].options[oIndex] = value;
    setCourseForm({ ...courseForm, test_questions: updated });
  };

  const removeQuestion = (index) => {
    setCourseForm({ ...courseForm, test_questions: courseForm.test_questions.filter((_, i) => i !== index) });
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="certification-courses-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Badges & Certifications</h1>
          <p className="text-[var(--text-muted)]">Create badges and courses for workers to earn</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'badges' && (
            <Button onClick={() => setShowBadgeCreateModal(true)} className="btn-primary" data-testid="create-badge-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Badge
            </Button>
          )}
          {activeTab === 'courses' && (
            <Button onClick={() => { resetCourseForm(); setEditingCourse(null); setShowCourseModal(true); }} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" /> Create Course
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-[var(--background-subtle)] rounded-lg p-1 mb-6">
        <button
          onClick={() => setActiveTab('badges')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'badges' ? 'bg-[var(--background-paper)] shadow text-[var(--primary)]' : 'text-[var(--text-muted)]'
          }`}
          data-testid="badges-tab"
        >
          <Award className="w-4 h-4" />
          Badges ({customBadges.length})
        </button>
        <button
          onClick={() => setActiveTab('courses')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'courses' ? 'bg-[var(--background-paper)] shadow text-[var(--primary)]' : 'text-[var(--text-muted)]'
          }`}
          data-testid="courses-tab"
        >
          <GraduationCap className="w-4 h-4" />
          Courses ({courses.length})
        </button>
      </div>

      {/* Badges Tab Content */}
      {activeTab === 'badges' && (
        <div className="space-y-4">
          {/* Info Card */}
          <div className="card p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30">
            <div className="flex items-start gap-3">
              <Award className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-[#D4AF37]">How Badges Work</p>
                <ul className="mt-1 space-y-1 text-[var(--text-secondary)]">
                  <li>• Workers can earn badges by completing courses</li>
                  <li>• You can also manually assign badges to workers</li>
                  <li>• Badges show on worker profiles and help match them to jobs</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Badges Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {customBadges.map(badge => {
              const BadgeIcon = BADGE_CONFIG[badge.id]?.icon || Award;
              const badgeColor = BADGE_CONFIG[badge.id]?.color || 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30';
              return (
                <div key={badge.id} className="card p-4 border-l-4 border-[#D4AF37]">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${badgeColor.split(' ')[0]}`}>
                        <BadgeIcon className={`w-6 h-6 ${badgeColor.split(' ')[1]}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold">{badge.name}</h3>
                        <p className="text-sm text-[var(--text-muted)]">{badge.description || 'No description'}</p>
                      </div>
                    </div>
                    {badge.is_custom && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-[var(--error)]"
                        onClick={() => handleDeleteBadge(badge.id)}
                        data-testid={`delete-badge-${badge.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  {/* Show linked course if any */}
                  {courses.find(c => c.badge_id === badge.id) && (
                    <div className="mt-3 pt-3 border-t border-[var(--border-primary)]">
                      <p className="text-xs text-[var(--text-muted)] flex items-center gap-1">
                        <GraduationCap className="w-3 h-3" />
                        Linked to: {courses.find(c => c.badge_id === badge.id)?.title}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {customBadges.length === 0 && (
            <div className="text-center py-12 bg-[var(--background-subtle)] rounded-xl">
              <Award className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-[var(--text-muted)]">No badges yet. Create your first badge!</p>
            </div>
          )}
        </div>
      )}

      {/* Courses Tab Content */}
      {activeTab === 'courses' && (
        <div>
          {/* Badge Legend */}
          <div className="card p-4 mb-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Award className="w-5 h-5" /> Available Service Badges</h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(BADGE_CONFIG).map(([id, config]) => (
                <div key={id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${config.color}`}>
                  <config.icon className="w-5 h-5" />
                  <span className="font-medium">{config.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Workers with Badge Management */}
          <div className="card p-4 mb-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Users className="w-5 h-5" /> Manage Worker Badges</h3>
            <p className="text-sm text-[var(--text-muted)] mb-4">Click on a worker to assign or revoke badges manually</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {workers?.filter(w => w.is_active !== false).map(worker => (
                <div 
                  key={worker.id}
                  onClick={() => openBadgeModal(worker)}
                  className="flex items-center gap-3 p-3 bg-[var(--background-subtle)] rounded-lg cursor-pointer hover:bg-[var(--background-subtle)] transition-colors"
                >
                  <div className="w-10 h-10 bg-[var(--primary)]/10 rounded-full flex items-center justify-center">
                    <span className="font-bold text-[var(--primary)]">{worker.full_name?.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{worker.full_name}</p>
                    <div className="flex gap-1 mt-1">
                      {worker.service_badges?.map(badge => {
                        const config = BADGE_CONFIG[badge.badge_id];
                        if (!config) return null;
                        return (
                          <span key={badge.badge_id} className={`w-5 h-5 rounded-full flex items-center justify-center ${config.color}`} title={config.label}>
                            <config.icon className="w-3 h-3" />
                          </span>
                        );
                      })}
                      {(!worker.service_badges || worker.service_badges.length === 0) && (
                        <span className="text-xs text-gray-400">No badges</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              ))}
            </div>
          </div>

          {/* Courses List */}
          <div className="mb-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><GraduationCap className="w-5 h-5" /> Certification Courses</h3>
            {loading ? (
              <div className="card p-8 text-center text-[var(--text-muted)]">Loading courses...</div>
            ) : courses.length === 0 ? (
              <div className="card p-12 text-center">
                <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-[var(--text-muted)]">No certification courses yet. Create one to get started.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {courses.map(course => {
                  const badgeConfig = BADGE_CONFIG[course.badge_id];
                  return (
                    <div key={course.id} className="card overflow-hidden">
                      <div className={`h-2 ${badgeConfig?.color?.split(' ')[0] || 'bg-[var(--background-subtle)]'}`} />
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-bold">{course.title}</h4>
                          {badgeConfig && (
                            <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${badgeConfig.color}`}>
                              <badgeConfig.icon className="w-3 h-3" />
                              {badgeConfig.label}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[var(--text-muted)] mb-3 line-clamp-2">{course.description || 'No description'}</p>
                        
                        {/* Course content summary */}
                        <div className="flex items-center gap-4 text-xs text-[var(--text-muted)] mb-3">
                          <span className="flex items-center gap-1">
                            <PlayCircle className="w-3.5 h-3.5" /> {course.videos?.length || 0} videos
                          </span>
                          <span className="flex items-center gap-1">
                            <FileText className="w-3.5 h-3.5" /> {course.policies?.length || 0} policies
                          </span>
                          <span className="flex items-center gap-1">
                            <ClipboardCheck className="w-3.5 h-3.5" /> {course.test_questions?.length || 0} questions
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] mb-3">
                          <span>Pass: {course.passing_score}%</span>
                          {course.is_required && <span className="px-2 py-0.5 bg-[var(--error)]/15 text-red-700 rounded">Required</span>}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => viewCourseProgress(course)} className="flex-1">
                            <Users className="w-3.5 h-3.5 mr-1" /> Progress
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openEditCourse(course)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteCourse(course.id)} className="text-[var(--error)]">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Badge Creation Modal */}
      {showBadgeCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowBadgeCreateModal(false)}>
          <div className="bg-[var(--background-paper)] rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Award className="w-6 h-6 text-[var(--primary)]" />
              Create New Badge
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label>Badge Name *</Label>
                <Input 
                  value={newBadgeForm.name}
                  onChange={e => setNewBadgeForm({...newBadgeForm, name: e.target.value})}
                  placeholder="e.g., Service Specialist"
                  data-testid="badge-name-input"
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={newBadgeForm.description}
                  onChange={e => setNewBadgeForm({...newBadgeForm, description: e.target.value})}
                  placeholder="What does this badge represent?"
                  rows={2}
                />
              </div>
              
              <div>
                <Label>Icon</Label>
                <Select value={newBadgeForm.icon} onValueChange={v => setNewBadgeForm({...newBadgeForm, icon: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="award">Award</SelectItem>
                    <SelectItem value="star">Star</SelectItem>
                    <SelectItem value="home">Home</SelectItem>
                    <SelectItem value="heart">Heart</SelectItem>
                    <SelectItem value="package">Package</SelectItem>
                    <SelectItem value="truck">Truck</SelectItem>
                    <SelectItem value="shield">Shield</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Color</Label>
                <Select value={newBadgeForm.color} onValueChange={v => setNewBadgeForm({...newBadgeForm, color: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gold">Gold</SelectItem>
                    <SelectItem value="blue">Blue</SelectItem>
                    <SelectItem value="purple">Purple</SelectItem>
                    <SelectItem value="green">Green</SelectItem>
                    <SelectItem value="amber">Amber</SelectItem>
                    <SelectItem value="pink">Pink</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowBadgeCreateModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleCreateBadge} className="btn-primary flex-1" data-testid="save-badge-btn">
                <Check className="w-4 h-4 mr-2" /> Create Badge
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Course Modal */}
      {showCourseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCourseModal(false)}>
          <div className="bg-[var(--background-paper)] rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-[var(--background-paper)] border-b p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">{editingCourse ? 'Edit Course' : 'Create Certification Course'}</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowCourseModal(false)}><X className="w-5 h-5" /></Button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Course Title *</Label>
                  <Input value={courseForm.title} onChange={e => setCourseForm({...courseForm, title: e.target.value})} placeholder="e.g., Service Certification" />
                </div>
                <div className="md:col-span-2">
                  <Label>Description</Label>
                  <Textarea value={courseForm.description} onChange={e => setCourseForm({...courseForm, description: e.target.value})} placeholder="What will workers learn?" rows={2} />
                </div>
                <div>
                  <Label>Badge Awarded</Label>
                  <Select value={courseForm.badge_id} onValueChange={v => setCourseForm({...courseForm, badge_id: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(BADGE_CONFIG).map(([id, config]) => (
                        <SelectItem key={id} value={id}>
                          <span className="flex items-center gap-2"><config.icon className="w-4 h-4" /> {config.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Passing Score (%)</Label>
                  <Input type="number" value={courseForm.passing_score} onChange={e => setCourseForm({...courseForm, passing_score: parseInt(e.target.value)})} min={50} max={100} />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="is_required" checked={courseForm.is_required} onChange={e => setCourseForm({...courseForm, is_required: e.target.checked})} />
                  <label htmlFor="is_required" className="text-sm">Required for all workers</label>
                </div>
              </div>

              {/* Videos Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2"><PlayCircle className="w-4 h-4" /> Training Videos</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addVideo}><Plus className="w-3 h-3 mr-1" /> Add Video</Button>
                </div>
                {courseForm.videos.map((video, idx) => (
                  <div key={video.id} className="flex gap-2 mb-2 p-3 bg-[var(--background-subtle)] rounded-lg">
                    <Input placeholder="Video Title" value={video.title} onChange={e => updateVideo(idx, 'title', e.target.value)} className="flex-1" />
                    <Input placeholder="Video URL" value={video.url} onChange={e => updateVideo(idx, 'url', e.target.value)} className="flex-1" />
                    <Input type="number" placeholder="Min" value={video.duration_minutes} onChange={e => updateVideo(idx, 'duration_minutes', parseInt(e.target.value))} className="w-20" />
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeVideo(idx)} className="text-[var(--error)]"><X className="w-4 h-4" /></Button>
                  </div>
                ))}
                {courseForm.videos.length === 0 && <p className="text-sm text-gray-400">No videos added yet</p>}
              </div>

              {/* Policies Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2"><FileText className="w-4 h-4" /> Policies to Acknowledge</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addPolicy}><Plus className="w-3 h-3 mr-1" /> Add Policy</Button>
                </div>
                {courseForm.policies.map((policy, idx) => (
                  <div key={policy.id} className="mb-2 p-3 bg-[var(--background-subtle)] rounded-lg">
                    <div className="flex gap-2 mb-2">
                      <Input placeholder="Policy Title" value={policy.title} onChange={e => updatePolicy(idx, 'title', e.target.value)} className="flex-1" />
                      <Button type="button" size="icon" variant="ghost" onClick={() => removePolicy(idx)} className="text-[var(--error)]"><X className="w-4 h-4" /></Button>
                    </div>
                    <Textarea placeholder="Policy content..." value={policy.content} onChange={e => updatePolicy(idx, 'content', e.target.value)} rows={2} />
                  </div>
                ))}
                {courseForm.policies.length === 0 && <p className="text-sm text-gray-400">No policies added yet</p>}
              </div>

              {/* Test Questions Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2"><ClipboardCheck className="w-4 h-4" /> Test Questions</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addQuestion}><Plus className="w-3 h-3 mr-1" /> Add Question</Button>
                </div>
                {courseForm.test_questions.map((q, qIdx) => (
                  <div key={q.id} className="mb-3 p-3 bg-[var(--background-subtle)] rounded-lg">
                    <div className="flex gap-2 mb-2">
                      <Input placeholder="Question" value={q.question} onChange={e => updateQuestion(qIdx, 'question', e.target.value)} className="flex-1" />
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeQuestion(qIdx)} className="text-[var(--error)]"><X className="w-4 h-4" /></Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {q.options.map((opt, oIdx) => (
                        <div key={`question-${q.id}-option-${oIdx}`} className="flex items-center gap-2">
                          <input 
                            type="radio" 
                            name={`correct-${q.id}`} 
                            checked={q.correct_answer === oIdx}
                            onChange={() => updateQuestion(qIdx, 'correct_answer', oIdx)}
                            className="text-[var(--success)]"
                          />
                          <Input placeholder={`Option ${oIdx + 1}`} value={opt} onChange={e => updateQuestionOption(qIdx, oIdx, e.target.value)} className="flex-1" />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Select the radio button for the correct answer</p>
                  </div>
                ))}
                {courseForm.test_questions.length === 0 && <p className="text-sm text-gray-400">No test questions added yet</p>}
              </div>
            </div>

            <div className="sticky bottom-0 bg-[var(--background-paper)] border-t p-4 flex gap-2">
              <Button variant="outline" onClick={() => setShowCourseModal(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSaveCourse} className="flex-1 btn-primary">{editingCourse ? 'Update Course' : 'Create Course'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Badge Assignment Modal */}
      {showBadgeModal && selectedWorker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowBadgeModal(false)}>
          <div className="bg-[var(--background-paper)] rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-2">Manage Badges</h2>
            <p className="text-[var(--text-muted)] mb-4">{selectedWorker.full_name}</p>
            
            <div className="space-y-3">
              {Object.entries(BADGE_CONFIG).map(([badgeId, config]) => {
                const hasBadge = workerBadges.some(b => b.id === badgeId);
                const badgeRecord = workerBadges.find(b => b.id === badgeId);
                
                return (
                  <div key={badgeId} className={`flex items-center justify-between p-3 rounded-lg border ${hasBadge ? config.color : 'bg-[var(--background-subtle)] border-[var(--border-primary)]'}`}>
                    <div className="flex items-center gap-3">
                      <config.icon className="w-6 h-6" />
                      <div>
                        <p className="font-medium">{config.label}</p>
                        {hasBadge && badgeRecord && (
                          <p className="text-xs opacity-70">
                            {badgeRecord.earned_via === 'course' ? 'Earned via course' : 'Manually assigned'}
                          </p>
                        )}
                      </div>
                    </div>
                    {hasBadge ? (
                      <Button size="sm" variant="ghost" onClick={() => revokeBadge(badgeId)} className="text-[var(--error)] hover:text-[var(--error)]">
                        Revoke
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => assignBadge(badgeId)}>
                        <Plus className="w-3 h-3 mr-1" /> Assign
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
            
            <Button variant="outline" onClick={() => setShowBadgeModal(false)} className="w-full mt-4">Close</Button>
          </div>
        </div>
      )}

      {/* Course Progress Modal */}
      {selectedCourse && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedCourse(null)}>
          <div className="bg-[var(--background-paper)] rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-2">Course Progress</h2>
            <p className="text-[var(--text-muted)] mb-4">{selectedCourse.title}</p>
            
            {courseProgress.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-muted)]">
                <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No workers enrolled yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {courseProgress.map(progress => (
                  <div key={progress.id} className="flex items-center justify-between p-3 bg-[var(--background-subtle)] rounded-lg">
                    <div>
                      <p className="font-medium">{progress.worker?.full_name || 'Unknown'}</p>
                      <p className="text-xs text-[var(--text-muted)]">{progress.worker?.email}</p>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        progress.status === 'completed' ? 'bg-[var(--success)]/15 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {progress.status === 'completed' ? `Passed (${progress.test_score}%)` : 'In Progress'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <Button variant="outline" onClick={() => setSelectedCourse(null)} className="w-full mt-4">Close</Button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default CertificationCoursesTab;
